/**
 * Security Hooks for AmalQ
 * React Query hooks for security operations including password changes,
 * 2FA management, and account activity
 * Following enterprise-grade patterns from existing codebase
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { securityApi } from '../services/securityApi';
import { profileQueryKeys } from './profileQueryKeys';
import {
  ChangePasswordRequest,
  EnableTwoFactorRequest,
  VerifyTwoFactorRequest,
  DisableTwoFactorRequest,
  ActivityHistoryRequest,
} from '../services/securityApi';

/**
 * Query Keys for Security Operations
 */
export const securityQueryKeys = {
  all: ['security'] as const,
  twoFactorStatus: () => [...securityQueryKeys.all, '2fa-status'] as const,
  activityHistory: (params?: ActivityHistoryRequest) => [...securityQueryKeys.all, 'activity', params] as const,
  trustedDevices: () => [...securityQueryKeys.all, 'trusted-devices'] as const,
};

/**
 * Hook: Get 2FA status
 * Fetches current 2FA configuration and status
 */
export function useTwoFactorStatus() {
  return useQuery({
    queryKey: securityQueryKeys.twoFactorStatus(),
    queryFn: securityApi.getTwoFactorStatus,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Get account activity history
 * Fetches user's account activity with filtering options
 */
export function useActivityHistory(params?: ActivityHistoryRequest) {
  return useQuery({
    queryKey: securityQueryKeys.activityHistory(params),
    queryFn: () => securityApi.getActivityHistory(params),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Change password mutation
 * Handles password change with proper error handling
 */
export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ChangePasswordRequest) => securityApi.changePassword(request),
    onSuccess: () => {
      // Invalidate security queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: securityQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('Password change failed:', error);
    },
  });
}

/**
 * Hook: Enable 2FA mutation
 * Handles 2FA setup initiation
 */
export function useEnableTwoFactor() {
  return useMutation({
    mutationFn: (request: EnableTwoFactorRequest) => securityApi.enableTwoFactor(request),
    onError: (error) => {
      console.error('2FA enable failed:', error);
    },
  });
}

/**
 * Hook: Verify 2FA setup mutation
 * Handles 2FA verification code submission
 */
export function useVerifyTwoFactor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: VerifyTwoFactorRequest) => securityApi.verifyTwoFactor(request),
    onSuccess: () => {
      // Invalidate 2FA status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: securityQueryKeys.twoFactorStatus() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('2FA verification failed:', error);
    },
  });
}

/**
 * Hook: Disable 2FA mutation
 * Handles 2FA disabling with password verification
 */
export function useDisableTwoFactor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DisableTwoFactorRequest) => securityApi.disableTwoFactor(request),
    onSuccess: () => {
      // Invalidate 2FA status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: securityQueryKeys.twoFactorStatus() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('2FA disable failed:', error);
    },
  });
}

/**
 * Hook: Generate backup codes mutation
 * Handles backup codes generation for 2FA recovery
 */
export function useGenerateBackupCodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => securityApi.generateBackupCodes(),
    onSuccess: () => {
      // Invalidate 2FA status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: securityQueryKeys.twoFactorStatus() });
    },
    onError: (error) => {
      console.error('Backup codes generation failed:', error);
    },
  });
}

/**
 * Hook: Resend 2FA code mutation
 * Handles resending verification codes
 */
export function useResendTwoFactorCode() {
  return useMutation({
    mutationFn: () => securityApi.resendTwoFactorCode(),
    onError: (error) => {
      console.error('Resend 2FA code failed:', error);
    },
  });
}

/**
 * Hook: Get trusted devices
 * Fetches user's trusted devices list
 */
export function useTrustedDevices() {
  return useQuery({
    queryKey: securityQueryKeys.trustedDevices(),
    queryFn: () => securityApi.getTrustedDevices(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Revoke device trust mutation
 * Handles revoking trust from a specific device
 */
export function useRevokeDeviceTrust() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: { deviceFingerprint: string }) => securityApi.revokeDeviceTrust(request),
    onSuccess: () => {
      // Invalidate trusted devices to refetch fresh data
      queryClient.invalidateQueries({ queryKey: securityQueryKeys.trustedDevices() });
    },
    onError: (error) => {
      console.error('Device trust revocation failed:', error);
    },
  });
}

/**
 * Combined security hook for easy access to all security operations
 * Provides a unified interface for security-related functionality
 */
export function useSecurity(): {
  // Queries
  twoFactorStatus: ReturnType<typeof useTwoFactorStatus>;
  activityHistory: ReturnType<typeof useActivityHistory>;
  trustedDevices: ReturnType<typeof useTrustedDevices>;
  
  // Mutations
  changePassword: ReturnType<typeof useChangePassword>;
  enableTwoFactor: ReturnType<typeof useEnableTwoFactor>;
  verifyTwoFactor: ReturnType<typeof useVerifyTwoFactor>;
  disableTwoFactor: ReturnType<typeof useDisableTwoFactor>;
  generateBackupCodes: ReturnType<typeof useGenerateBackupCodes>;
  resendTwoFactorCode: ReturnType<typeof useResendTwoFactorCode>;
  revokeDeviceTrust: ReturnType<typeof useRevokeDeviceTrust>;
  
  // Loading states
  isLoading: boolean;
  isChangingPassword: boolean;
  isEnabling2FA: boolean;
  isVerifying2FA: boolean;
  isDisabling2FA: boolean;
  isGeneratingBackupCodes: boolean;
  isResendingCode: boolean;
} {
  const twoFactorStatus = useTwoFactorStatus();
  const activityHistory = useActivityHistory();
  
  const changePassword = useChangePassword();
  const enableTwoFactor = useEnableTwoFactor();
  const verifyTwoFactor = useVerifyTwoFactor();
  const disableTwoFactor = useDisableTwoFactor();
  const generateBackupCodes = useGenerateBackupCodes();
  const resendTwoFactorCode = useResendTwoFactorCode();
  
  const trustedDevices = useTrustedDevices();
  const revokeDeviceTrust = useRevokeDeviceTrust();

  return {
    // Queries
    twoFactorStatus,
    activityHistory,
    trustedDevices,
    
    // Mutations
    changePassword,
    enableTwoFactor,
    verifyTwoFactor,
    disableTwoFactor,
    generateBackupCodes,
    resendTwoFactorCode,
    revokeDeviceTrust,
    
    // Loading states
    isLoading: twoFactorStatus.isLoading || activityHistory.isLoading,
    isChangingPassword: changePassword.isPending,
    isEnabling2FA: enableTwoFactor.isPending,
    isVerifying2FA: verifyTwoFactor.isPending,
    isDisabling2FA: disableTwoFactor.isPending,
    isGeneratingBackupCodes: generateBackupCodes.isPending,
    isResendingCode: resendTwoFactorCode.isPending,
  };
}
